import {
  type GetSavedItemsParams,
  SavedItem,
  SavedItems,
  SavedItemId,
} from "@repo/apis/customers-saveditems-v3";

export const SAVED_ITEMS_SORT_OPTIONS = {
  DATE_ADDED: "date",
  PRICE: "price",
  DISCOUNT: "discount",
  STOCK_LEVEL: "stockLevel",
  RECENTLY_DISCOUNTED: "recentlyDiscounted",
} as const;

export const SAVED_ITEMS_SORT_ORDER = {
  ASC: "asc",
  DSC: "dsc",
} as const;

export const DEFAULT_PAGINATION = {
  LIMIT: 100,
  OFFSET: 0,
} as const;

type SavedItemsSortOptions = Pick<
  GetSavedItemsParams,
  "sort" | "sortOrder" | "limit" | "offset"
>;

export const createGetSavedItemsOptions = (
  overrides?: SavedItemsSortOptions,
): SavedItemsSortOptions => ({
  limit: DEFAULT_PAGINATION.LIMIT,
  offset: DEFAULT_PAGINATION.OFFSET,
  sort: SAVED_ITEMS_SORT_OPTIONS.DATE_ADDED,
  sortOrder: SAVED_ITEMS_SORT_ORDER.DSC,
  ...overrides,
});

const matchesProduct = (
  item: { productId: number; variantId?: number | null },
  productId: number,
  variantId?: number,
): boolean => {
  return (
    item.productId === productId && (!variantId || item.variantId === variantId)
  );
};

export const isProductSaved = (
  items: SavedItems,
  productId: number,
  variantId?: number,
): boolean => {
  const validSavedItemIds = filterValidSavedItemIds(items);
  const idMatch = validSavedItemIds.some((item) =>
    matchesProduct(item, productId, variantId),
  );

  if (idMatch) return true;

  return items.savedItemProducts.some((item) =>
    matchesProduct(item, productId, variantId),
  );
};

export const findSavedItem = (
  items: SavedItems,
  productId: number,
  variantId?: number,
): SavedItem | undefined => {
  return items.savedItemProducts.find((item) =>
    matchesProduct(item, productId, variantId),
  );
};

export const findSavedItemId = (
  items: SavedItems,
  productId: number,
  variantId?: number,
): { id: string } | undefined => {
  const validSavedItemIds = filterValidSavedItemIds(items);
  const foundId = validSavedItemIds.find((item) =>
    matchesProduct(item, productId, variantId),
  );

  if (foundId) {
    return { id: foundId.id };
  }

  const foundItem = items.savedItemProducts.find((item) =>
    matchesProduct(item, productId, variantId),
  );

  return foundItem ? { id: foundItem.id } : undefined;
};

export const getInvalidProductIds = (items: SavedItems): number[] => {
  const errorMessages =
    items.Errors?.filter(
      (error) => error.errorCode === "NotFoundProductId",
    ).map((error) => error.errorMessage) || [];

  const productIdRegex = /ProductId '(\d+)'/g;
  const invalidIds: number[] = [];

  errorMessages.forEach((message) => {
    let match;
    while ((match = productIdRegex.exec(message)) !== null) {
      if (match[1]) {
        invalidIds.push(parseInt(match[1], 10));
      }
    }
  });

  return invalidIds;
};

export const filterValidSavedItemIds = (items: SavedItems): SavedItemId[] => {
  const invalidIds = getInvalidProductIds(items);
  return items.savedItemIds.filter(
    (item) => !invalidIds.includes(item.productId),
  );
};

export const normalizeApiResponse = (response: SavedItems): SavedItems => {
  const validSavedItemIds = filterValidSavedItemIds(response);

  return {
    ...response,
    savedItemIds: validSavedItemIds,
    itemCount: Math.max(
      response.savedItemProducts.length,
      validSavedItemIds.length,
    ),
  };
};
